import React, { useState } from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import { useNavigation } from "../../../hooks";
import { useTransmissionDemandDetail } from "../../../hooks/useTransmissionDemandDetail";
import { deleteTransmissionDemand } from "../../../services/institutionService";
import LoadingSpinner from "../../common/LoadingSpinner";
import DataDeletionConfirmModal from "../../modals/DataDeletionConfirmModal";
import Button from "../../ui/Button";
import Card from "../../ui/Card";
import Icon from "../../ui/Icon";

const TransmissionDemandDetailPage: React.FC = () => {
  const { transmissionDemandId } = useParams<{
    transmissionDemandId: string;
  }>();
  const { goBack } = useNavigation();
  const { detail, loading } = useTransmissionDemandDetail(
    transmissionDemandId || ""
  );
  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  if (!transmissionDemandId) {
    return (
      <div className="h-screen-dvh bg-white flex items-center justify-center">
        <p className="text-gray-500">잘못된 접근입니다.</p>
      </div>
    );
  }

  const handleDisconnectClick = () => {
    setIsDisconnectModalOpen(true);
  };

  const handleDisconnectConfirm = async (deleteData: boolean) => {
    try {
      setIsDeleting(true);
      await deleteTransmissionDemand(transmissionDemandId, deleteData);
      toast.success("전송요구서가 철회되었습니다.");
      setIsDisconnectModalOpen(false);
      goBack();
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "전송요구서 철회에 실패했습니다.";
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleModify = () => {
    if (!detail || !transmissionDemandId) return;
    // 수정 페이지로 이동
    window.location.href = `/settings/transmission-demand/${transmissionDemandId}/edit`;
  };

  const InfoRow: React.FC<{ label: string; value: string }> = ({
    label,
    value,
  }) => (
    <div className="py-3">
      <p className="text-sm text-gray-500 mb-1">{label}</p>
      <p className="text-base text-gray-800">{value}</p>
    </div>
  );

  return (
    <div className="h-screen-dvh bg-white flex flex-col">
      {/* 상단 헤더 */}
      <header className="bg-white px-4 py-3 flex items-center">
        <button
          onClick={goBack}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <Icon name="chevron-left" className="text-gray-600" />
        </button>      </header>

      {/* 메인 콘텐츠 */}
      <main className="flex-1 p-4 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner />
          </div>
        ) : detail ? (
          <>
            <Card>
              <div className="divide-y divide-gray-200">
                <InfoRow
                  label="보내는 곳"
                  value={detail.provideInstitutionName}
                />
                <InfoRow
                  label="받는 곳"
                  value={detail.recipientOrganizationName}
                />
                <InfoRow
                  label="사용목적"
                  value={detail.transmissionDemandPurpose}
                />
                <InfoRow
                  label="정기적 전송 요구 여부 및 주기"
                  value={
                    detail.regularTransferCycle === "1/w"
                      ? "주 1회 업데이트"
                      : "업데이트하지 않음"
                  }
                />
                <InfoRow
                  label="전송 요구 시작일"
                  value={detail.transmissionDemandAgreementBeginDate}
                />
                <InfoRow
                  label="전송 요구 종료일"
                  value={detail.transmissionDemandAgreementEndDate}
                />
                <InfoRow
                  label="보유 기간"
                  value={"서비스 이용 종료 시 또는 삭제 요구 시까지"}
                />
              </div>
            </Card>

            {/* 하단 버튼 */}
            <div className="mt-6 space-y-3">
              <Button
                onClick={handleDisconnectClick}
                variant="outline"
                className="w-full border-red-300 text-red-600 hover:bg-red-50"
                disabled={isDeleting}
              >
                {isDeleting ? "철회 중..." : "연결 해제"}
              </Button>
              <Button
                onClick={handleModify}
                variant="primary"
                className="w-full"
                disabled={isDeleting}
              >
                연결 내용 수정
              </Button>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500">
              전송요구서 정보를 불러올 수 없습니다.
            </p>
          </div>
        )}
      </main>

      {/* 연결 해제 확인 모달 */}
      <DataDeletionConfirmModal
        isOpen={isDisconnectModalOpen}
        onClose={() => setIsDisconnectModalOpen(false)}
        onConfirm={handleDisconnectConfirm}
        title="연결 해제"
        institutionName={detail?.provideInstitutionName}
        confirmText="연결 해제"
        cancelText="취소"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default TransmissionDemandDetailPage;
